import { Component, Inject, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';

import { UploadDownloadService } from '@shared/services/upload-download.service';
import { ManualVideo } from '@shared/models/entities/userManual.model';

/** Módulo al que se puede amarrar un video. */
export interface ManualModuleOption {
  id:    string | null;
  label: string;
}

export interface ManualVideoDialogData {
  modules:  ManualModuleOption[];
  roleName: string;
  /** Path lógico en S3 donde se guardan los videos de este rol. */
  uploadPath: string;
  /** Usuario que sube (para el sello `uploadedBy`). */
  uploadedBy: string | null;
  /** Video existente cuando se está editando; null al agregar. */
  video?: ManualVideo | null;
}

/**
 * Alta/edición de un video del manual de un rol.
 *
 * El archivo se sube a S3 en cuanto se elige (endpoint `/uploadPDF`), y lo
 * que se guarda en el registro del manual es la key que devuelve, no el
 * archivo. El diálogo devuelve el `ManualVideo` ya listo para agregarse a la
 * lista del manual.
 */
@Component({
  selector: 'app-manual-video-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MaterialModule],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>smart_display</mat-icon>
      {{ data.video ? 'Editar video' : 'Agregar video' }} — {{ data.roleName }}
    </h2>

    <mat-dialog-content class="mat-typography">
      <form [formGroup]="form" class="video-form">

        <mat-form-field appearance="outline" class="full-col">
          <mat-label>Título del video</mat-label>
          <input matInput formControlName="title" maxlength="120"
                 placeholder="Ej. Cómo levantar una intervención">
          @if (form.get('title')?.touched && form.get('title')?.invalid) {<mat-error>El título es obligatorio.</mat-error>}
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-col">
          <mat-label>Módulo</mat-label>
          <mat-select formControlName="moduleId">
            @for (option of data.modules; track option.label) {
              <mat-option [value]="option.id ?? option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          <mat-hint>Los videos se agrupan por módulo dentro del manual del rol.</mat-hint>
          @if (form.get('moduleId')?.touched && form.get('moduleId')?.invalid) {<mat-error>Selecciona el módulo del video.</mat-error>}
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-col">
          <mat-label>Descripción (opcional)</mat-label>
          <textarea matInput rows="2" formControlName="description"></textarea>
        </mat-form-field>

      </form>

      <!-- ── Archivo ───────────────────────────────────────────── -->
      <div class="file-box" [class.has-file]="!!fileKey()">
        @if (uploading()) {
          <div class="file-status">
            <mat-spinner diameter="22"></mat-spinner>
            <span>Subiendo video… no cierres esta ventana.</span>
          </div>
        } @else if (fileKey()) {
          <div class="file-status">
            <mat-icon class="ok-icon">check_circle</mat-icon>
            <div class="file-info">
              <span class="file-name">{{ fileName() }}</span>
              <span class="file-hint">Archivo cargado</span>
            </div>
            <button mat-stroked-button (click)="picker.click()">Reemplazar</button>
          </div>
        } @else {
          <div class="file-status">
            <mat-icon class="up-icon">cloud_upload</mat-icon>
            <div class="file-info">
              <span class="file-name">Sin archivo</span>
              <span class="file-hint">MP4, WebM o MOV</span>
            </div>
            <button mat-raised-button color="primary" (click)="picker.click()">Elegir video</button>
          </div>
        }

        <input #picker type="file" hidden accept="video/*" (change)="onFileSelected($event)">
      </div>

      @if (errorMsg()) {
        <p class="error-msg">{{ errorMsg() }}</p>
      }

      <p class="size-note">
        <mat-icon>info_outline</mat-icon>
        La subida pasa por el API (API Gateway limita la petición a ~10 MB).
        Para videos largos conviene recortarlos o comprimirlos antes.
      </p>

    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-raised-button mat-dialog-close [disabled]="uploading()">Cancelar</button>
      <button mat-raised-button color="accent" (click)="save()"
              [disabled]="uploading() || !fileKey()">
        Guardar
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2 { display: flex; align-items: center; gap: 8px; color: #170f5f; font-weight: bold; }
    .video-form { display: grid; grid-template-columns: 1fr; gap: 4px; }
    .video-form mat-form-field { width: 100%; }
    .full-col { grid-column: 1 / -1; }

    .file-box {
      border: 1px dashed #e0e0e0; border-radius: 10px;
      padding: 14px 16px; margin-top: 8px; background: #fafafa;
    }
    .file-box.has-file { border-style: solid; border-color: #1bb152; background: #edfcf3; }
    .file-status { display: flex; align-items: center; gap: 12px; }
    .file-info { display: flex; flex-direction: column; flex: 1 1 auto; min-width: 0; }
    .file-name { font-size: 14px; font-weight: 600; color: #212121; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .file-hint { font-size: 12px; color: #757575; }
    .ok-icon { color: #1bb152; }
    .up-icon { color: #757575; }

    .error-msg { color: #1010eb; font-size: 13px; margin: 10px 0 0; }
    .size-note {
      display: flex; align-items: flex-start; gap: 6px;
      font-size: 12px; color: #555555; margin: 12px 0 0;
    }
    .size-note mat-icon { font-size: 16px; width: 16px; height: 16px; margin-top: 1px; }
  `],
})
export class ManualVideoDialogComponent {

  private formBuilder = inject(FormBuilder);
  private uploads     = inject(UploadDownloadService);

  readonly uploading = signal(false);
  readonly errorMsg  = signal<string>('');
  readonly fileKey   = signal<string>('');
  readonly fileName  = signal<string>('');

  /**
   * Id del video. Se calcula ANTES de subir porque se usa como prefijo del
   * archivo en S3: la key es `path/folio/<nombre>` y dos videos del mismo rol
   * con el mismo nombre de archivo se pisarían entre sí (y borrar uno dejaría
   * al otro apuntando a una key inexistente).
   */
  private readonly videoId: string;

  form: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<ManualVideoDialogComponent, ManualVideo | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: ManualVideoDialogData,
  ) {
    const video = data.video ?? null;
    this.videoId = video?.id ?? crypto.randomUUID();

    this.form = this.formBuilder.group({
      title:       [video?.title ?? null, Validators.required],
      moduleId:    [video?.moduleId ?? video?.module ?? null, Validators.required],
      description: [video?.description ?? null],
    });

    if (video?.key) {
      this.fileKey.set(video.key);
      this.fileName.set(video.fileName || this.baseName(video.key));
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.errorMsg.set('');
    this.uploading.set(true);

    try {
      // Se sube con el id como prefijo para que la key sea única dentro del
      // rol, y se conserva el nombre original para mostrarlo en la tarjeta.
      const uploaded = await this.uploads.upload({
        path:  this.data.uploadPath,
        folio: 'videos',
        file:  new File([file], `${this.videoId}-${file.name}`, { type: file.type }),
      });
      this.fileKey.set(uploaded.filename);
      this.fileName.set(file.name);
    } catch (err: any) {
      console.error('[Manuales] Error subiendo el video:', err);
      this.errorMsg.set(`No se pudo subir el video: ${err?.message ?? err}`);
    } finally {
      this.uploading.set(false);
      // Permite volver a elegir el mismo archivo si el intento falló.
      input.value = '';
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.fileKey()) {
      this.errorMsg.set('Falta cargar el archivo del video.');
      return;
    }

    const value = this.form.value;
    const selected = this.data.modules.find(m => (m.id ?? m.label) === value.moduleId);

    this.dialogRef.close({
      id:          this.videoId,
      title:       String(value.title).trim(),
      description: value.description ? String(value.description).trim() : null,
      moduleId:    selected?.id ?? null,
      module:      selected?.label ?? String(value.moduleId),
      key:         this.fileKey(),
      fileName:    this.fileName(),
      uploadedOn:  this.data.video?.uploadedOn ?? this.timestamp(),
      uploadedBy:  this.data.video?.uploadedBy ?? this.data.uploadedBy,
    });
  }

  /**
   * Fecha con el MISMO formato que el backend (`epione_time.now()`):
   * "MM/DD/YYYY, HH:MM:SS" en 24 horas. Con `toLocaleString('en-US')` salía
   * en formato AM/PM, que no parsea el `TO_TIMESTAMP` del historial.
   */
  private timestamp(): string {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${now.getFullYear()}, `
      + `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  }

  private baseName(key: string): string {
    const parts = key.split('/');
    return parts[parts.length - 1] || key;
  }
}
